import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import { LEAD_ARCHIVE_ACTIVE_REASONS_OPTION_ITEMS } from 'common/leadConstants';

import { hideFieldIf, createFormValidationFn } from 'client/helpers/formikHelpers';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_RED_INVERTED,
} from 'client/components/SrButton/SrButton';
import SrModalV2, { MODAL_THEME_STACK } from 'client/components/SrModalV2/SrModalV2';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrSeparator from 'client/components/SrSeparator';

const ARCHIVE_REASON_WITH_QUESTION = LEAD_ARCHIVE_ACTIVE_REASONS_OPTION_ITEMS.filter(
  ({ question }) => question,
).map(({ value }) => value);

const getFormFields = ({ values = {} } = {}) => {
  const formFields = [
    {
      id: 'archiveReasons',
      type: 'tags',
      title: 'Reasons for Archive (Internal)',
      properties: {
        choices: LEAD_ARCHIVE_ACTIVE_REASONS_OPTION_ITEMS,
      },
      validations: ['required', 'nonEmptyArray'],
    },
  ];

  if (values.archiveReasons?.length) {
    for (const reason of ARCHIVE_REASON_WITH_QUESTION) {
      if (values.archiveReasons.includes(reason)) {
        formFields.push({
          id: `archiveReasonsDescription.${reason}`,
          type: 'short_text',
          title: LEAD_ARCHIVE_ACTIVE_REASONS_OPTION_ITEMS.find(({ value }) => value === reason)
            ?.question,
          subtitle: (
            <SrBox position="relative" top="-10px" marginBottom="-11px">
              <SrText color="red" size="16" weight="600" font="greycliff">
                THIS WILL BE VISIBLE TO THE PARTNER
              </SrText>
            </SrBox>
          ),
          properties: {
            placeholder: 'Write here...',
          },
        });
      }
    }
  }

  formFields.push(
    hideFieldIf(!values.archiveReasons?.some((reason) => reason === 'other'), {
      id: 'partnerArchiveReason',
      type: 'long_text',
      title: 'Reason for the Partner',
      subtitle: (
        <SrBox position="relative" top="-10px" marginBottom="-11px">
          <SrText color="red" size="16" weight="600" font="greycliff">
            THIS WILL BE VISIBLE TO THE PARTNER
          </SrText>
        </SrBox>
      ),
      properties: {
        placeholder: 'Reason for dropping out...',
      },
      validations: ['required', 'archiveReasonForPartner'],
    }),
  );

  return formFields;
};

const SrArchiveListingLeadModal = ({ onClose, onSubmit }) => {
  return (
    <SrModalV2
      showCloseButton={false}
      contentMaxWidth="759px"
      contentMaxHeight="720px"
      contentPadding="20px 40px 30px 30px"
      padding="0"
      margin="2vh auto 0"
      theme={MODAL_THEME_STACK}
    >
      <Formik
        initialValues={{
          archiveReasons: [],
          archiveReasonsDescription: {},
        }}
        onSubmit={onSubmit}
        validate={(formValues) => {
          const formFields = getFormFields({ values: formValues });
          return createFormValidationFn(formFields)(formValues);
        }}
        render={(formikBag) => {
          const formFields = getFormFields({
            values: formikBag.values,
          });

          return (
            <Form>
              <SrText
                font="greycliff"
                weight="800"
                size="26"
                lineHeight="33px"
                align="center"
                width="100%"
                display="block"
              >
                Archive Listing For This Lead
              </SrText>
              {formFields.map((formField, index) => (
                <SrBox key={formField.id} margin="10px 0">
                  <SrFormikField
                    formField={formField}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  {index === 0 && <SrSeparator marginTop="30px" />}
                </SrBox>
              ))}
              <SrFlex justify="between">
                <SrBox width="48%">
                  <SrButton theme={SR_BUTTON_THEME_ADOBE_GREY} type="button" onClick={onClose}>
                    Cancel
                  </SrButton>
                </SrBox>
                <SrBox width="48%">
                  <SrButton
                    theme={SR_BUTTON_THEME_ADOBE_RED_INVERTED}
                    type="submit"
                    disabled={!formikBag.isValid}
                  >
                    Archive
                  </SrButton>
                </SrBox>
              </SrFlex>
            </Form>
          );
        }}
      />
    </SrModalV2>
  );
};

SrArchiveListingLeadModal.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

SrArchiveListingLeadModal.displayName = 'SrArchiveListingLeadModal';

export default SrArchiveListingLeadModal;
